/**
 * Boîtes de dialogue applicatives (U1, polish alpha) — remplacent les
 * `window.confirm` / `window.prompt` natifs, moches et bloquants, par
 * des modales cohérentes avec le design system (`modalTemplate`).
 *
 * API **promise-based**, montée sur `document.body` comme le host des
 * toasts (indépendante des vues, survit aux re-renders) :
 *
 *   - `confirmDialog(opts): Promise<boolean>` — OK / Annuler.
 *   - `promptDialog(opts): Promise<string | null>` — saisie texte,
 *     `null` si annulé.
 *
 * Escape / clic backdrop / bouton Annuler → résolvent négativement.
 * Le focus initial va au champ (prompt) ou au bouton de confirmation.
 */
import { html, render, type TemplateResult } from 'lit-html';
import { ref } from 'lit-html/directives/ref.js';
import { modalTemplate } from './Modal.js';
import { t } from '../i18n/index.js';
import styles from './Dialog.module.css';

interface BaseOpts {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style d'action risquée (bouton rouge). */
  danger?: boolean;
}

let host: HTMLElement | null = null;

/** Monte le host des dialogs. Idempotent. */
export function mountDialogHost(): () => void {
  if (host) return () => undefined;
  host = document.createElement('div');
  host.setAttribute('data-testid', 'dialog-host');
  document.body.appendChild(host);
  return () => {
    host?.remove();
    host = null;
  };
}

function clear(): void {
  if (host) render(html``, host);
}

function renderDialog(content: TemplateResult, onClose: () => void, testid: string): void {
  if (!host) mountDialogHost();
  render(modalTemplate({ title: '', content, onClose, closeLabel: t('close'), testid }), host!);
}

/** Confirmation OK / Annuler. Résout `true` si confirmé. */
export function confirmDialog(opts: BaseOpts): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (v: boolean): void => {
      clear();
      resolve(v);
    };
    const content = html`<div class=${styles.dialog}>
      <h2 class=${styles.title}>${opts.title}</h2>
      ${opts.message ? html`<p class=${styles.message}>${opts.message}</p>` : null}
      <div class=${styles.actions}>
        <button
          type="button"
          class=${styles.btnGhost}
          data-testid="dialog-cancel"
          @click=${(): void => done(false)}
        >
          ${opts.cancelLabel ?? t('cancel')}
        </button>
        <button
          type="button"
          class=${opts.danger ? styles.btnDanger : styles.btnPrimary}
          data-testid="dialog-confirm"
          ${ref((el) => (el as HTMLElement | undefined)?.focus())}
          @click=${(): void => done(true)}
        >
          ${opts.confirmLabel ?? t('confirm')}
        </button>
      </div>
    </div>`;
    renderDialog(content, () => done(false), 'dialog-confirm-modal');
  });
}

/** Saisie texte. Résout la valeur, ou `null` si annulé. */
export function promptDialog(
  opts: BaseOpts & { defaultValue?: string; placeholder?: string },
): Promise<string | null> {
  return new Promise((resolve) => {
    let value = opts.defaultValue ?? '';
    const done = (v: string | null): void => {
      clear();
      resolve(v);
    };
    const onSubmit = (e: Event): void => {
      e.preventDefault();
      done(value.trim() === '' ? null : value.trim());
    };
    const content = html`<form class=${styles.dialog} @submit=${onSubmit}>
      <h2 class=${styles.title}>${opts.title}</h2>
      ${opts.message ? html`<p class=${styles.message}>${opts.message}</p>` : null}
      <input
        type="text"
        class=${styles.input}
        .value=${value}
        placeholder=${opts.placeholder ?? ''}
        data-testid="dialog-input"
        ${ref((el) => (el as HTMLInputElement | undefined)?.focus())}
        @input=${(e: Event): void => {
          value = (e.target as HTMLInputElement).value;
        }}
      />
      <div class=${styles.actions}>
        <button
          type="button"
          class=${styles.btnGhost}
          data-testid="dialog-cancel"
          @click=${(): void => done(null)}
        >
          ${opts.cancelLabel ?? t('cancel')}
        </button>
        <button type="submit" class=${styles.btnPrimary} data-testid="dialog-confirm">
          ${opts.confirmLabel ?? t('save')}
        </button>
      </div>
    </form>`;
    renderDialog(content, () => done(null), 'dialog-prompt-modal');
  });
}

/** @internal pour tests. */
export function _resetDialogForTests(): void {
  clear();
  host?.remove();
  host = null;
}
